/**
 * List export (docs/11 F-4; docs/00 Tier 1; ADR-23's column vocabulary).
 *
 * `data.export` has been granted to four of the six base roles since 0004 and
 * nothing implemented it — the same "capability nobody can spend" shape as the
 * role CRUD X-8 closed, and as the agreement PATCH with no screen.
 *
 * ===========================================================================
 * The design, in one sentence: the export IS the list
 * ===========================================================================
 *
 * The export runs the SAME list query the screen runs, under the caller's own
 * RLS context, and serialises the columns the column selector already names.
 * Not a second query that happens to return similar rows — the same one, paged
 * through to the end.
 *
 * That is not tidiness. An export is the highest-consequence read in the app:
 * it takes data out of every guard the app has and puts it in a spreadsheet
 * somebody will email. A separate "export query" is a second place for the
 * WHERE clause to be right, and the day the two disagree is the day an export
 * quietly returns rows the screen would not show. Reusing the list query makes
 * that failure impossible rather than unlikely.
 *
 * Two consequences worth stating:
 *
 *   * **Filters carry.** Export what you are looking at — the same `?q=`,
 *     `?status=`, `?typeId=` the URL holds (docs/03 §1). An export that ignored
 *     the filters would be a different question's answer.
 *   * **Sensitive values cannot leak, because they are not in the vocabulary.**
 *     Security codes, `federal_ein` and the access notes are not columns in any
 *     catalog — they live behind an audited REVEAL (docs/01 §5). The export can
 *     only name catalog columns, so it cannot reach them. `SENSITIVE_NEVER`
 *     below is the belt to that braces, and a unit test fails if a catalog ever
 *     grows a column whose id matches one.
 */

import { z } from 'zod';

import { TABLE_CATALOGS, type TableColumnDef, type TableId } from './table-layouts.ts';

/**
 * Columns that exist to be LOOKED at rather than read — there is no cell value
 * a spreadsheet could hold. A photo is the only one today.
 *
 * Declared rather than inferred, so a new image-ish column is a decision
 * somebody makes here instead of a blank column somebody notices in Excel.
 */
export const NOT_EXPORTABLE: Readonly<Record<TableId, readonly string[]>> = {
  properties: ['photo'],
  tenants: [],
  vendors: [],
};

/**
 * Field names that must never become a column, in any catalog, ever.
 *
 * These are the values docs/01 §5 puts behind an audited reveal and docs/14 Q-1
 * keeps out of the search index. A CSV has no reveal and no audit trail — once
 * a code is in a spreadsheet it is in an email — so the rule here is absolute
 * rather than permission-shaped. Enforced by test, not by review.
 */
export const SENSITIVE_NEVER: readonly string[] = [
  'federalEin',
  'ein',
  'securityCode',
  'securityCodes',
  'lockboxLocation',
  'lockboxCode',
  'accessNotes',
  'accessControlSystem',
  'alarmCode',
  'pinCode',
  'roofHatchNotes',
];

/** The columns of a table that an export can actually carry. */
export function exportableColumns(tableId: TableId): readonly TableColumnDef[] {
  const skip = new Set(NOT_EXPORTABLE[tableId]);
  return TABLE_CATALOGS[tableId].filter((column) => !skip.has(column.id));
}

export const exportQuerySchema = z.object({
  /**
   * Comma-separated column ids. Absent means the table's default view, which is
   * what somebody who has never opened the column selector is looking at — so
   * "export what I see" is true for them too.
   */
  columns: z.string().optional(),
});

/**
 * Resolve the requested columns against the catalog.
 *
 * Unknown ids are DROPPED rather than rejected: a stale bookmark or an older
 * client naming a column that has since been renamed should still produce the
 * export, not a 400 nobody can act on. A request that names nothing valid falls
 * back to the default view rather than producing a file with no columns.
 */
export function resolveExportColumns(
  tableId: TableId,
  requested: string | undefined,
): readonly TableColumnDef[] {
  const available = exportableColumns(tableId);
  if (requested === undefined || requested.trim() === '') {
    return available.filter((column) => column.locked === true || column.defaultOn === true);
  }
  const ids = requested.split(',').map((id) => id.trim());
  const wanted = new Set(ids);
  // REQUEST order, because the request now carries the reader's chosen column
  // order (docs/04 §5). This used to take catalog order with the comment "the
  // file's columns match the screen's" — a promise that stopped being true the
  // moment columns could be reordered, which is the kind of quiet divergence
  // between a file and a screen that makes people stop trusting exports.
  const byId = new Map(available.map((column) => [column.id, column]));
  const picked = ids
    .map((id) => byId.get(id))
    .filter((column): column is TableColumnDef => column !== undefined);
  if (picked.length === 0) {
    return available.filter((column) => column.locked === true || column.defaultOn === true);
  }
  // A locked column is always in the table and is always in the file: it is
  // what identifies a row, and a spreadsheet of unidentifiable rows is waste.
  const lockedMissing = available.filter(
    (column) => column.locked === true && !wanted.has(column.id),
  );
  return lockedMissing.length === 0 ? picked : [...lockedMissing, ...picked];
}

/**
 * Leading characters a spreadsheet skips before it decides what a cell IS.
 *
 * TWO SETS, because neither covers the other.
 *
 *   * **ECMAScript whitespace**, via `\s` — space, tab, CR, LF, vertical tab,
 *     form feed, and the non-ASCII ones that are easy to forget: U+00A0
 *     no-break space, U+1680, U+2000–U+200A, U+2028, U+2029, U+202F, U+205F,
 *     U+3000 and the U+FEFF byte-order mark.
 *   * **The remaining C0 controls and DEL**, which `\s` does NOT match — NUL,
 *     SOH and the rest up to U+001F, plus U+007F. A code-point compare rather
 *     than a regex, because a regex would need the control characters written
 *     inside it and eslint refuses that for good reason.
 *
 * The non-ASCII half was missing until 2026-09-19 (mailbox RM-065's review of
 * `949bc8d4`), and the omission was not academic: a marker behind U+00A0 or
 * U+FEFF reached the file unneutralized. It also made this function disagree
 * with the quoting step below, which uses `trim()` — and `trim` removes exactly
 * the `\s` set. One serializer cannot hold two opinions about what leading
 * whitespace is.
 */
function isCsvLeadingNoise(ch: string): boolean {
  if (/\s/.test(ch)) return true;
  const code = ch.codePointAt(0);
  if (code === undefined) return false;
  return code <= 0x20 || code === 0x7f;
}

/**
 * The characters that make a spreadsheet treat a cell as something to EVALUATE
 * rather than something to read: docs/18 §7b's governed set.
 *
 * None of them is a letter, so "case-insensitively for command forms" needs no
 * casing logic — `=cmd|…` and `=CMD|…` are both caught by their `=`.
 */
const CSV_FORMULA_MARKERS = new Set(['=', '+', '-', '@']);

/**
 * Does this TEXT open a formula, once the spreadsheet has skipped what it
 * skips? docs/18 §7b rule 3.
 *
 * Two ways to qualify, and the second is the one a naive first-character test
 * misses. Either the first SIGNIFICANT character is a marker, or the leading
 * run itself contains a tab or a carriage return — §7b names those as markers
 * in their own right, because a cell that starts with one arrives at Excel with
 * the control stripped and whatever followed it promoted to the front.
 */
// Exported for the import preview (RM-065 item 6): one detector decides both
// what the export neutralizes and what the preview calls out, so the two can
// never disagree about which text a spreadsheet would evaluate.
export function opensFormula(text: string): boolean {
  let i = 0;
  while (i < text.length && isCsvLeadingNoise(text[i] ?? '')) {
    const ch = text[i];
    if (ch === '\t' || ch === '\r') return true;
    i += 1;
  }
  return CSV_FORMULA_MARKERS.has(text[i] ?? '');
}

/**
 * One CSV cell, RFC 4180 — and spreadsheet-safe (docs/18 §7b, mailbox RM-065).
 *
 * Quoted whenever the value holds a comma, a quote, a newline or leading or
 * trailing space — and internal quotes doubled. A tenant called `Smith, Jones &
 * Co` is not exotic, and an address holds commas by definition.
 *
 * **QUOTING ALONE IS NOT PROTECTION**, which is the belief RM-065 was about. A
 * quoted cell beginning `=` is still a formula to Excel, and Excel is the
 * consumer this file is written for. So formula-capable TEXT gets a leading
 * apostrophe — the governed neutralization — and it is applied BEFORE the RFC
 * quoting below, never after, so the emitted cell is quoted as what it actually
 * contains.
 *
 * What is NOT neutralized: a genuine `number` or `boolean`. `-5` is a negative
 * number and keeps its sign as a number. The discriminator is the TYPE, not the
 * shape of the text — so the STRING `'-5'` is text that begins with a marker and
 * IS neutralized, which is what §7b rule 4 means by distinguishing a `-` that
 * begins a number from one that begins text.
 *
 * Nothing here touches stored data. This is a pure serializer on a read path;
 * the export route never writes to a business table.
 *
 * TWO CONSEQUENCES, RECORDED RATHER THAN DISCOVERED. The apostrophe MAY BE
 * VISIBLE when the file is opened — accepted, per §7b rule 5. And the
 * operational CSV therefore makes **no reversible round-trip promise**: a
 * stored value that genuinely begins with `'` is indistinguishable from a
 * neutralized one once emitted. The lossless editable artifact is typed XLSX,
 * which is deferred (docs/10 ADR-69); this file is not it.
 *
 * `null` becomes empty rather than the string "null": a blank cell is what a
 * missing value looks like to a reader, and "null" is what a bug looks like.
 */
export function csvCell(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  // A genuine number or boolean is a typed value, never a formula: neutralizing
  // it would turn every negative number in the file into text.
  const isText = typeof value === 'string';
  const raw = isText ? value : String(value);
  if (raw === '') return '';
  // Before the quoting, and the original text is kept verbatim after the
  // prefix — no trim, no normalization, nothing the reader did not type.
  const text = isText && opensFormula(raw) ? `'${raw}` : raw;
  const needsQuotes = /[",\r\n]/.test(text) || text !== text.trim();
  return needsQuotes ? `"${text.replaceAll('"', '""')}"` : text;
}

/**
 * A CSV document.
 *
 * CRLF line endings, because RFC 4180 says so and because Excel on Windows —
 * which is what TKC opens these in — treats a bare LF file as one long row in
 * some import paths.
 *
 * A UTF-8 BOM is prepended for the same reason: without it Excel reads the file
 * as the system codepage, and a property named "Peña Station" arrives as
 * "PeÃ±a Station". The BOM is invisible to every other consumer.
 */
export function toCsv(headers: readonly string[], rows: readonly (readonly unknown[])[]): string {
  const lines = [headers.map((h) => csvCell(h)).join(',')];
  for (const row of rows) {
    lines.push(row.map((cell) => csvCell(cell as string | number | boolean | null)).join(','));
  }
  // Written as an escape rather than as a literal BOM in the source: an
  // invisible character in a template literal is one careless editor away from
  // being deleted or duplicated, and neither shows up in review.
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

/**
 * The file name a browser saves.
 *
 * Dated, because the first thing anybody asks about a spreadsheet on their
 * desktop is when it was pulled, and the second is what it was filtered to —
 * which the file cannot answer, so the date is the part worth carrying.
 */
export function exportFilename(tableId: TableId, isoDate: string): string {
  return `propos-${tableId}-${isoDate.slice(0, 10)}.csv`;
}
